import { Container, Divider, Grid, Space, Text, Title } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import { OpenComponent } from '../../enums/misc.enum';
import { Currency } from '../../enums/retrieve-transaction.enums';
import { getCardTypeName } from '../../helpers/common.helpers';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { ErrorResponse } from '../../type/error-response.types';
import { RetrieveMIDResponse } from '../../type/mids.types';
import StatusText from '../admin-table/components/status-text.component';
import BackButton from '../buttons/back-button.component';
import XCol3 from '../x-components/grid-col/x-col-3.component';
import XTextStackInfo from '../x-components/x-text-stack-info.component';
import ViewMIDSkeleton from './components/view-mid-skeleton.component';

function ViewMIDs(props: Props) {
  const { setComponentManager, selectedMIDId, setSelectedMIDId } = props;
  const [isLoading, setIsLoading] = useState(true);
  const axiosPrivate = useAxiosPrivate();
  const [midData, setMIDData] = useState<RetrieveMIDResponse>();
  const defaultNumber = '0.00';

  useEffect(() => {
    const loadSelectedMID = async () => {
      try {
        setIsLoading(true);

        const response = await axiosPrivate.get(`/web/mids/${selectedMIDId}`);
        const midData: RetrieveMIDResponse = response.data;

        setMIDData(midData);
      } catch (err: any) {
        const error: ErrorResponse = err.response?.data;

        showNotification({
          id: 'failed-to-retrieve-mid',
          message:
            error?.error ||
            err.response?.data?.error ||
            'An Unexpected Error Occurred',
          icon: <X size={16} />,
          color: 'red',
          autoClose: 10000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadSelectedMID();
  }, []);

  const labels = {
    partner: 'Partner',
    reseller: 'Reseller',
    merchant: 'Merchant',
    midName: 'MID name',
    switch: 'Switch',
    mcc: 'MCC',

    switchName: 'Switch',
    cardType: 'Card type',
    currency: 'Currency',
    isActive: 'Active status',
    merchantId: 'Merchant ID',

    xpayMarkup: 'Xpay mark up',
    acquirerBuyRate: 'Acquirer Buy Rate',
    decline: 'Decline',

    xpayPerTransactionAddOn: 'Per Transaction Add-on (XPAY)',
    clientPerTransactionAddOn: 'Per Transaction Add-on (Client)',
    fraudControlFee: 'Fraud control fee',
    preAuthRate: 'Pre auth rate',
    captureRate: 'Capture rate',
    finalMerchantRate: 'Final merchant rate',

    clientMarkup: ' Client mark up',
    refundRate: 'Refund rate',
    voidRate: 'Void rate',

    cashBackRate: 'Cash back rate',
    balanceInquiryRate: 'Balance inquiry rate',

    midPemFile: 'MID PEM File',
  };
  return (
    <>
      <BackButton
        handleClick={() => {
          setComponentManager(OpenComponent.HOME);
          setSelectedMIDId(undefined);
        }}
      />
      <Title order={2} my="lg">
        MID Information
      </Title>

      <Divider my="xl" />
      <Container fluid px={0}>
        {isLoading ? (
          <ViewMIDSkeleton skeletonWidth={'50%'} />
        ) : (
          <>
            <Grid>
              <XCol3>
                <XTextStackInfo
                  label={labels.partner}
                  value={midData?.association.partnerName}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.reseller}
                  value={midData?.association.resellerName}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.merchant}
                  value={midData?.association.merchantName}
                />
              </XCol3>
              <XCol3>
                <></>
              </XCol3>

              <XCol3>
                <XTextStackInfo
                  label={labels.midName}
                  value={midData?.midName}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.switchName}
                  value={midData?.switchName}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.cardType}
                  value={getCardTypeName(midData?.cardType)}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.currency}
                  value={
                    midData?.currency === Currency.PHP
                      ? 'PHP'
                      : midData?.currency === Currency.USD
                        ? 'USD'
                        : undefined
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.merchantId}
                  value={midData?.params?.param2}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.mcc}
                  value={midData?.params?.mcc}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.isActive}
                  value={
                    <StatusText
                      status={midData?.isActive ? 'ENABLED' : 'DISABLED'}
                    />
                  }
                />
              </XCol3>
            </Grid>

            <Divider my="xl" />

            <Text size="xl" weight={700}>
              Rates
            </Text>
            <Space h="xl" />

            <Grid>
              <XCol3>
                <XTextStackInfo
                  label={labels.balanceInquiryRate}
                  value={
                    (midData?.rates.balanceInquiryRate?.toFixed(2) ??
                      defaultNumber) + '%'
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.captureRate}
                  value={
                    (midData?.rates.captureRate?.toFixed(2) ?? defaultNumber) +
                    '%'
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.acquirerBuyRate}
                  value={
                    (midData?.rates.acquirerBuyRate?.toFixed(2) ??
                      defaultNumber) + '%'
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.cashBackRate}
                  value={
                    (midData?.rates.cashBackRate?.toFixed(2) ?? defaultNumber) +
                    '%'
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.xpayMarkup}
                  value={
                    (midData?.rates.xpayMarkup?.toFixed(2) ?? defaultNumber) +
                    '%'
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.decline}
                  value={midData?.rates.declineRate?.toFixed(2)}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.xpayPerTransactionAddOn}
                  value={midData?.rates.xpayPerTransactionAddOn?.toFixed(2)}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.clientPerTransactionAddOn}
                  value={midData?.rates.clientPerTransactionAddOn?.toFixed(2)}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.preAuthRate}
                  value={
                    (midData?.rates.preAuthRate?.toFixed(2) ?? defaultNumber) +
                    '%'
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.clientMarkup}
                  value={
                    (midData?.rates.clientMarkup?.toFixed(2) ?? defaultNumber) +
                    '%'
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.refundRate}
                  value={
                    (midData?.rates.refundRate?.toFixed(2) ?? defaultNumber) +
                    '%'
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.voidRate}
                  value={
                    (midData?.rates.voidRate?.toFixed(2) ?? defaultNumber) + '%'
                  }
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.fraudControlFee}
                  value={midData?.rates.fraudControlFee?.toFixed(2)}
                />
              </XCol3>
              <XCol3>
                <XTextStackInfo
                  label={labels.finalMerchantRate}
                  value={
                    (
                      Number(midData?.rates.acquirerBuyRate ?? defaultNumber) +
                      Number(midData?.rates.xpayMarkup ?? defaultNumber) +
                      Number(midData?.rates.clientMarkup ?? defaultNumber)
                    ).toFixed(2) +
                    '% + ' +
                    (
                      Number(
                        midData?.rates.xpayPerTransactionAddOn ?? defaultNumber,
                      ) +
                      Number(
                        midData?.rates.clientPerTransactionAddOn ??
                          defaultNumber,
                      )
                    ).toFixed(2)
                  }
                />
              </XCol3>
            </Grid>
            {midData?.switch === 6 && midData?.files ? (
              <>
                <Divider my="xl" />

                <Text size="xl" weight={700}>
                  Files
                </Text>
                <Space h="xl" />
                <Grid>
                  <Grid.Col>
                    <XTextStackInfo
                      label={labels.midPemFile}
                      value={midData?.files[0].name}
                    />
                  </Grid.Col>
                </Grid>
              </>
            ) : (
              <> </>
            )}
          </>
        )}
      </Container>
    </>
  );
}

export default ViewMIDs;

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  selectedMIDId: number;
  setSelectedMIDId: React.Dispatch<React.SetStateAction<number | undefined>>;
}
